import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import BlockingOverlay from './BlockingOverlay';

// A no-op default, deliberately. useFetch reads this context unconditionally, and it
// is exercised in dozens of unit tests that render a hook with no provider around it.
// A null default would make every one of those a crash.
const NOOP = { begin: () => {}, end: () => {} };

export const BlockingLoadContext = createContext(NOOP);

// One reference count, one overlay, one call site. Before this, nine pages each decided
// on their own whether to render <Spinner /> off a shared boolean, and nothing stopped
// two of them being right at the same time. Counting rather than flagging is also what
// keeps the overlay up until the LAST request settles instead of the first.
export function BlockingLoadProvider({ children }) {
  const [count, setCount] = useState(0);

  const begin = useCallback(() => setCount(n => n + 1), []);
  // Clamped: a finally that fires twice must not leave the count below zero, or the
  // next begin() would land on 0 and show nothing.
  const end = useCallback(() => setCount(n => Math.max(0, n - 1)), []);

  const value = useMemo(() => ({ begin, end }), [begin, end]);

  return (
    <BlockingLoadContext.Provider value={value}>
      {children}
      {count > 0 && <BlockingOverlay />}
    </BlockingLoadContext.Provider>
  );
}

// Holds the overlay across a sequence of requests that the user experiences as one
// action. Saving a product is the case this exists for: the write, then the refetch
// that shows its result. Marking both `blocking: true` would let the count fall to
// zero in between and flash the overlay off mid-save.
//
// This is the only sanctioned way to open a span by hand. Call sites still never
// touch begin/end themselves -- the pairing lives in the finally below, where an
// exception cannot skip it.
export function useBlockingSpan() {
  const blocking = useContext(BlockingLoadContext);

  return useCallback(async run => {
    blocking.begin();
    try {
      return await run();
    } finally {
      blocking.end();
    }
  }, [blocking]);
}
